import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { isNullOrUndefined } from '@uzurv/rideWss/app/util';
import { UZURV_API_CONFIGURATION, UzurvApiConfiguration } from '@uzurv/rideWss/configurations/uzurv-api.configuration';
import { Deserialize } from 'cerialize';
import { Observable, ReplaySubject, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import { CustomEncoder } from './custom-encoder';

@Injectable({
  providedIn: 'root'
})
export class UzurvRestService {
  private httpClient: HttpClient = inject(HttpClient);

  readonly uzurvApiConfiguration: UzurvApiConfiguration = inject(UZURV_API_CONFIGURATION);

  /**
   * Override to change the default api url for the service.
   *
   * @default claraApiUrl
   */
  protected getApiUrl(): string {
    return this.uzurvApiConfiguration.claraApiUrl;
  }

  protected get<T>(url: string, params: object = {}, options: object = {}, type?: any): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    let httpParams: HttpParams;

    if (params) {
      httpParams = this.toHttpParams(params);
    }

    if (type) {
      return this.httpClient.get<T>(url, { params: httpParams, ...options }).pipe(map(response => Deserialize(response, type)));
    }

    return this.httpClient.get<T>(url, { params: httpParams, ...options });
  }

  protected getImage(url: string, params: object = {}): Observable<any> {
    const obs = new ReplaySubject<any>(1);

    this.get(url, params, { responseType: 'blob' }).subscribe(
      result => {
        const reader = new FileReader();
        reader.addEventListener(
          'load',
          () => {
            obs.next(reader.result);
            obs.complete();
          },
          false
        );
        if (result) {
          reader.readAsDataURL(result);
        }
      },
      err => {
        obs.error(err);
        obs.complete();
      }
    );

    return obs;
  }

  protected patch<T>(url: string, body: any, params: object = {}): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    let httpParams: HttpParams;

    if (params) {
      httpParams = this.toHttpParams(params);
    }

    return this.httpClient.patch<T>(url, body, { params: httpParams });
  }

  protected put<T>(url: string, body: any, params: object = {}): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    let httpParams: HttpParams;

    if (params) {
      httpParams = this.toHttpParams(params);
    }

    return this.httpClient.put<T>(url, body, { params: httpParams });
  }

  // TODO: Add params to this method signature
  protected post<T>(url: string, body: any, options = {}): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    return this.httpClient.post<T>(url, body, options);
  }

  protected uploadWithProgress(url: string, file: any, fieldName = 'file', options = {}): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    const body = new FormData();
    body.append(fieldName, file);

    return this.httpClient.request('POST', url, { body: body, reportProgress: true, observe: 'events', ...options });
  }

  protected delete<T>(url: string, body?: HttpParams | { [param: string]: string | Array<string> }): Observable<any> {
    if (!url.startsWith(this.getApiUrl())) {
      url = this.getApiUrl() + url;
    }

    return this.httpClient.delete<T>(url, {
      params: body
    });
  }

  /**
   * Framework for handling generic HTTP errors from the server
   *
   * @param error Error that is returned from the server
   */
  protected handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      // console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      // console.error(
      //   `Backend returned code ${error.status}, ` +
      //   `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }

  /**
   * Convert Object to HttpParams, and filter out where you may have passed an empty key (Spring doesn't like that)
   *
   * @param obj
   */
  private toHttpParams(obj: object): HttpParams {
    return Object.getOwnPropertyNames(obj)
      .filter(key => {
        if (isNullOrUndefined(obj[key])) {
          return false;
        }
        if (Array.isArray(obj[key]) && obj[key].length === 0) {
          return false;
        }
        return true;
      })
      .reduce((p, key) => p.append(key, obj[key]), new HttpParams({ encoder: new CustomEncoder() }));
  }
}
